import {
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  output,
  model,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  ChevronDownIconComponent,
  ChevronUpIconComponent,
  ArrowLeftIconComponent,
} from '@kuiper/icons';
import { NgTemplateOutlet } from '@angular/common';
import { QuickActionComponent } from '../quick-action/quick-action.component';
import { QuickActionItem } from '../quick-action/quick-action.model';
import {
  MenuItem,
  SidenavAction,
  SidenavRoles,
  SidenavWidthBreakpoint,
} from './sidenav.model';
import { fromEvent, Subscription } from 'rxjs';

@Component({
  selector: 'lib-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    TranslocoPipe,
    ChevronDownIconComponent,
    ChevronUpIconComponent,
    ArrowLeftIconComponent,
    NgTemplateOutlet,
    QuickActionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SidenavComponent implements AfterViewInit, OnDestroy {
  router = inject(Router);
  cdr = inject(ChangeDetectorRef);

  sidenavRoles = SidenavRoles;
  sidenavAction = SidenavAction;

  menuItems = input.required<MenuItem[]>();
  footerItems = input<MenuItem[]>([]);
  model = input<keyof typeof SidenavRoles>(this.sidenavRoles.employee);
  showQuickAction = input<boolean>(false);
  quickActionItems = input<QuickActionItem[]>([]);

  handleAction = output<string>();

  isCollapsed = model(false);

  subscription = new Subscription();
  quickAction = viewChild<QuickActionComponent>('quickAction');
  ngAfterViewInit(): void {
    const currentRoute = this.router.url;
    this.menuItems().forEach(item => {
      if (
        item.path === currentRoute ||
        (item.path && currentRoute.includes(item.path))
      ) {
        item.active = true;
      }
    });
    this.cdr.detectChanges();

    this.handleResize();
  }

  handleResize() {
    this.subscription.add(
      fromEvent(globalThis, 'resize').subscribe(() => {
        if (window.innerWidth < SidenavWidthBreakpoint) {
          this.isCollapsed.set(true);
        } else {
          this.isCollapsed.set(false);
        }
      })
    );
  }

  toggleSidenav() {
    this.isCollapsed.set(!this.isCollapsed());
    this.menuItems().forEach(item => {
      if (item?.children?.length) {
        item.hasActiveChild = item.children.some(child => {
          item.expanded = item.hasActiveChild && !this.isCollapsed();
          return this.router.url === child.path && this.isCollapsed();
        });
      }
    });
  }

  collapseAllParentMenuItems() {
    this.menuItems().forEach(item => {
      if (item?.children?.length) {
        item.expanded = false;
      }
    });
  }

  toggleSubMenu(item: MenuItem) {
    if (item?.children?.length && !this.isCollapsed()) {
      item.expanded = !item.expanded;
    } else {
      this.onClickMenuItem();
      if (item?.children?.length) {
        this.isCollapsed.set(false);
        item.expanded = !item.expanded;
        this.expandItemHasActiveLink();
      }
    }

    if (item.action) {
      this.handleAction.emit(item.action);
    }
  }

  expandItemHasActiveLink() {
    this.menuItems().forEach(ele => {
      ele.expanded = ele.hasActiveChild || ele.expanded;
    });
  }

  onClickMenuItem() {
    this.menuItems().forEach(item => {
      item.active = true;
    });
    this.collapseSidenav();
  }

  collapseSidenav() {
    if (!this.isCollapsed() && window.innerWidth < SidenavWidthBreakpoint) {
      this.isCollapsed.set(true);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
